import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardMedia,
  Grid,
  IconButton,
  Dialog,
  DialogContent,
  useTheme,
  useMediaQuery
} from "@mui/material";
import { styled } from '@mui/material/styles';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CloseIcon from '@mui/icons-material/Close';
import FiberImg from "../assets/fiber.jpeg";
import TruckImg from "../assets/truck.jpeg";
import ServicesImg from "../assets/services.jpeg";

// Imagens de exemplo (substitua pelos caminhos reais)
// **Conteúdo atualizado para foco B2B**
const showcaseImages = [
  { 
    src: FiberImg, 
    alt: "Enterprise Fiber Optic Installation",
    title: "Enterprise Fiber Rollout",
    description: "Professional deployment and activation of high-capacity fiber optic networks for corporate and industrial premises."
  },
  { 
    src: TruckImg, 
    alt: "Dedicated Project Fleet",
    title: "Dedicated Project Fleet",
    description: "Our fully equipped vehicles and certified teams are mobilized exclusively for large-scale enterprise projects and infrastructure contracts."
  },
  { 
    src: ServicesImg, 
    alt: "Commercial Network Solutions",
    title: "Custom Network Solutions",
    description: "Tailored, end-to-end network infrastructure planning, installation, and maintenance, serving the specific needs of our business partners."
  },
];

// Componente estilizado para o card
const ShowcaseCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  overflow: 'hidden',
  width: '100%',
  maxWidth: 360,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.3s ease-in-out',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 16px 32px rgba(0, 0, 0, 0.15)',
    '& .media-overlay': {
      opacity: 1,
    },
    '& .card-content': {
      backgroundColor: theme.palette.primary.main,
      '& .MuiTypography-root': {
        color: 'white',
      },
    },
  },
}));

// Overlay para zoom
const MediaOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0,
  transition: 'opacity 0.3s ease-in-out',
  cursor: 'pointer',
}));

function Showcase() {
  const [selectedImage, setSelectedImage] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleOpen = (image) => {
    setSelectedImage(image);
  };

  const handleClose = () => {
    setSelectedImage(null);
  };

  return (
    <Box
      sx={{
        py: { xs: 6, md: 10 },
        px: { xs: 2, md: 4 },
        mx: 'auto',
        backgroundColor: '#0A2E3A',
      }}
    >
      <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 7 } }}>
        <Typography
          variant="h2"
          sx={{
            fontWeight: 700,
            mb: 2,
            fontSize: { xs: 32, md: 42 },
            color: '#3AC3E3',
          }}
        >
          {/* Título Atualizado */}
          Commercial Project Showcase
        </Typography>
        <Typography
          variant="h6"
          sx={{
            mb: 3,
            color: 'rgba(255, 255, 255, 0.8)',
            maxWidth: 600,
            mx: 'auto',
            lineHeight: 1.6,
            fontWeight: 400,
          }}
        >
          {/* Descrição Atualizada, enfatizando B2B */}
          Explore our completed enterprise installations. We specialize exclusively in providing high-quality connectivity solutions for other companies, leveraging our expertise to support your infrastructure needs. We do not serve individual residential customers.
        </Typography>
      </Box>
      
      <Grid container spacing={isMobile ? 1 : 2} justifyContent="center">
        {showcaseImages.map((img, idx) => (
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            key={idx}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'stretch',
            }}
          >
            <ShowcaseCard>
              <Box sx={{ position: 'relative' }}>
                <CardMedia
                  component="img"
                  image={img.src}
                  alt={img.alt}
                  sx={{
                    width: '100%',
                    height: 240,
                    objectFit: 'cover',
                  }}
                />
                <MediaOverlay 
                  className="media-overlay"
                  onClick={() => handleOpen(img)}
                >
                  <IconButton 
                    sx={{ 
                      color: 'white', 
                      backgroundColor: 'rgba(58, 195, 227, 0.8)',
                      '&:hover': {
                        backgroundColor: 'rgba(58, 195, 227, 1)',
                      }
                    }}
                  >
                    <ZoomInIcon />
                  </IconButton>
                </MediaOverlay>
              </Box>
              
              <Box 
                className="card-content"
                sx={{ 
                  p: 3, 
                  flexGrow: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease-in-out',
                  backgroundColor: '#05020B',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                    color: 'white',
                    transition: 'color 0.3s ease-in-out',
                  }}
                >
                  {img.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    lineHeight: 1.5,
                    transition: 'color 0.3s ease-in-out',
                  }}
                >
                  {img.description}
                </Typography>
              </Box>
            </ShowcaseCard>
          </Grid>
        ))}
      </Grid>

      {/* Modal para visualização ampliada */}
      <Dialog
        open={!!selectedImage}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
            backgroundColor: 'transparent',
            boxShadow: 'none',
          }
        }}
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          {selectedImage && (
            <>
              <IconButton
                onClick={handleClose}
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  color: 'white',
                  zIndex: 10,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
              <CardMedia
                component="img"
                image={selectedImage.src}
                alt={selectedImage.alt}
                sx={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '80vh',
                  objectFit: 'contain',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
                  color: 'white',
                  p: 3,
                  pt: 6,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {selectedImage.title}
                </Typography>
                <Typography variant="body2">
                  {selectedImage.description}
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default Showcase;